import { CheckCircle2, Hourglass, ShoppingCart, XCircle } from 'lucide-react';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { usePermissions } from '@/features/auth';
import { useSelectedCountry } from '@/features/country-scope';
import { DateRangePicker, lastNDays } from '@/shared/components/date-range-picker';
import { ErrorState } from '@/shared/components/error-state';
import { PageContainer } from '@/shared/components/page-container';
import { PageHeader } from '@/shared/components/page-header';

import { useDashboard } from '../api/dashboard.queries';
import { AttentionPanel } from '../components/attention-panel';
import { FailureReasonsCard } from '../components/failure-reasons-card';
import { OrdersTrendChart } from '../components/orders-trend-chart';
import { QuickStats } from '../components/quick-stats';
import { RevenueCard } from '../components/revenue-card';
import { RevenueRankingCard } from '../components/revenue-ranking-card';
import { StatCard } from '../components/stat-card';
import { TopEntitiesChart, type TopEntity } from '../components/top-entities-chart';

const NO_BRANDS: TopEntity[] = [];

/**
 * Operations overview for the selected country and period, from one Admin API
 * call. What the staff member may not see — revenue, or a queue they do not
 * work — is left out by the backend, and left out here with it.
 */
export function DashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { country } = useSelectedCountry();
  const { can } = usePermissions();
  const fallback = useMemo(() => lastNDays(30), []);
  const range = {
    from: searchParams.get('from') ?? fallback.from,
    to: searchParams.get('to') ?? fallback.to,
  };

  const dashboard = useDashboard({ from: range.from, to: range.to, countryId: country?.id });
  const data = dashboard.data;
  const topBrands = useMemo(
    () => data?.topBrands.map((brand) => ({ name: brand.brandName, value: brand.orders })),
    [data],
  );

  const setRange = (value: { from: string; to: string }) => {
    const next = new URLSearchParams(searchParams);
    next.set('from', value.from);
    next.set('to', value.to);
    setSearchParams(next, { replace: true });
  };

  const stat = { isLoading: dashboard.isLoading, isError: dashboard.isError };

  return (
    <PageContainer>
      <PageHeader
        title="Dashboard"
        description={
          country
            ? `Orders and open work in ${country.nameEn ?? country.code ?? 'the selected country'} for the selected period.`
            : 'Orders and open work in your countries for the selected period.'
        }
        actions={<DateRangePicker value={range} onChange={setRange} />}
      />

      {dashboard.error && !data ? (
        <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} />
      ) : (
        <>
          <QuickStats stats={data?.quickStats} />

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Orders" value={data?.orders.total} icon={ShoppingCart} {...stat} />
            <StatCard
              label="Completed"
              value={data?.orders.completed}
              icon={CheckCircle2}
              {...stat}
            />
            <StatCard label="Failed" value={data?.orders.failed} icon={XCircle} {...stat} />
            <StatCard
              label="Processing"
              value={data?.orders.processing}
              icon={Hourglass}
              {...stat}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <OrdersTrendChart metric="orders" data={data?.trend} isLoading={dashboard.isLoading} />
            <OrdersTrendChart
              metric="completed"
              data={data?.trend}
              isLoading={dashboard.isLoading}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <TopEntitiesChart
              title="Top brands by completed orders"
              unit="orders"
              data={topBrands ?? (dashboard.isLoading ? undefined : NO_BRANDS)}
              isLoading={dashboard.isLoading}
            />
            {data ? <AttentionPanel attention={data.attention} /> : null}
          </div>

          <FailureReasonsCard
            reasons={data?.failureReasons}
            from={range.from}
            to={range.to}
            isLoading={dashboard.isLoading}
          />

          {data?.revenue ? <RevenueCard revenue={data.revenue} /> : null}

          {data?.topBrandsByRevenue || data?.topDenominationsByRevenue || data?.topCustomers ? (
            <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
              {data.topBrandsByRevenue ? (
                <RevenueRankingCard
                  title="Top brands by revenue"
                  rows={data.topBrandsByRevenue}
                  hrefFor={can('catalogue.read') ? (row) => `/brands/${row.id}` : undefined}
                />
              ) : null}
              {data.topDenominationsByRevenue ? (
                <RevenueRankingCard
                  title="Top denominations by revenue"
                  rows={data.topDenominationsByRevenue}
                />
              ) : null}
              {data.topCustomers ? (
                <RevenueRankingCard
                  title="Top customers by spend"
                  rows={data.topCustomers}
                  hrefFor={(row) => `/customers/${row.id}`}
                />
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </PageContainer>
  );
}
